import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Play,
  DollarSign,
  Calendar,
  Users,
  MoreHorizontal,
  MessageCircle,
  User,
  Plus,
  Bell,
  History,
  Settings,
  LogOut,
  Shield,
  CreditCard,
  X,
  ChevronRight,
  ShoppingBag,
  Sparkles,
  Camera,
  MapPin,
  Mail,
  Heart,
  UserPlus,
  Check,
  Loader2,
  Target,
  Trophy,
  Newspaper,
  Wallet,
  Coins,
  Lock,
  Search
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ShotTracerModal } from "./ShotTracer";
import { GolfShotsModal } from "./GolfShots";
import GolfNewsTicker from "./GolfNewsTicker";
import soundEffects from "../utils/soundEffects";
import { AccountLinkConfirmations } from './AccountLinkConfirmations';
import { HeaderUserName } from './HeaderUserName';
import { HeaderHandicap } from './HeaderHandicap';
import { useHeaderHandicap } from '../hooks/useHeaderHandicap';
import { hasHandicap } from '../utils/handicap';
import { usePinnedNavigation } from '../hooks/usePinnedNavigation';
import '../styles/pinned-navigation.css';

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Full Screen Context - allows child components to hide navigation
const FullScreenContext = createContext({
  isFullScreen: false,
  setIsFullScreen: () => {}
});

// Hook for child components to use full screen mode
export const useFullScreen = () => useContext(FullScreenContext);

// Notification type icons
const getNotificationIcon = (type) => {
  switch (type) {
    case 'event_invite':
      return Calendar;
    case 'buddy_request':
    case 'buddy_accepted':
      return UserPlus;
    case 'message':
      return MessageCircle;
    case 'like':
      return Heart;
    case 'comment':
      return MessageCircle;
    case 'game_invite':
      return Play;
    case 'settlement':
      return DollarSign;
    case 'community_invite':
    case 'community_join_request':
    case 'community_request_accepted':
      return Users;
    default:
      return Bell;
  }
};

// Notification type colors
const getNotificationColor = (type) => {
  switch (type) {
    case 'event_invite':
      return 'bg-green-500/20 text-green-500';
    case 'buddy_request':
    case 'buddy_accepted':
      return 'bg-green-500/20 text-green-400';
    case 'message':
      return 'bg-blue-500/20 text-blue-400';
    case 'like':
      return 'bg-pink-500/20 text-pink-400';
    case 'comment':
      return 'bg-purple-500/20 text-purple-400';
    case 'game_invite':
      return 'bg-orange-500/20 text-orange-400';
    case 'settlement':
      return 'bg-yellow-500/20 text-yellow-400';
    case 'community_invite':
    case 'community_join_request':
      return 'bg-cyan-500/20 text-cyan-400';
    case 'community_request_accepted':
      return 'bg-emerald-500/20 text-emerald-400';
    default:
      return 'bg-gray-500/20 text-gray-400';
  }
};

// Top Navigation Items - Premium Par Seeker (5 core sections)
const topNavItems = [
  { path: '/home', icon: Home, label: 'Home', unlockKey: null },
  { path: '/discover', icon: Users, label: 'Discover', unlockKey: null }, // Pay-to-view
  { path: '/games', icon: Trophy, label: 'Compete', unlockKey: null }, // Pay entry fees
  { path: '/events', icon: Calendar, label: 'Events', unlockKey: null }, // Pay to join
];

// Bottom Navigation Items - Simplified premium experience
const bottomNavItems = [
  { path: '/home', icon: Home, label: 'Home', unlockKey: null },
  { path: '/discover', icon: Users, label: 'Discover', unlockKey: null },
  { path: '/chat', icon: MessageCircle, label: 'Chat', unlockKey: null }, // Pay to message
  { path: '/profile', icon: User, label: 'Profile', unlockKey: null },
];

export const DarkAppShell = ({ 
  children, 
  user, 
  onLogout, 
  unreadMessages = 0,
  onCreatePost,
  milestones = null  // Add milestones prop for unlock checking
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showAIToolsSubmenu, setShowAIToolsSubmenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPSTracer, setShowPSTracer] = useState(false);
  const [showGolfShots, setShowGolfShots] = useState(false);
  const [showTickerSettings, setShowTickerSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const handicap = useHeaderHandicap(user?.user_id, location.key);
  const isHome = ['/home', '/dashboard', '/'].includes(location.pathname);
  const [golfCoins, setGolfCoins] = useState(0);
  const [localMilestones, setLocalMilestones] = useState(milestones);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { shellRef, headerRef, footerRef } = usePinnedNavigation(isFullScreen);
  const moreMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  
  // Fetch milestones if not provided
  const fetchMilestones = useCallback(async () => {
    if (milestones) {
      setLocalMilestones(milestones);
      return;
    }
    const token = localStorage.getItem('parseeker_session_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/milestones/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLocalMilestones(data);
      }
    } catch (error) {
      console.log('Could not fetch milestones');
    }
  }, [milestones]);
  
  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);
  
  // Check if feature is unlocked
  const isUnlocked = useCallback((unlockKey) => {
    if (!unlockKey) return true;
    if (!localMilestones) return true; // Default to unlocked if not loaded
    
    switch (unlockKey) {
      case 'find_golfers':
        return localMilestones.find_golfers_unlocked;
      case 'tournaments':
        return localMilestones.tournaments_unlocked;
      case 'outings':
        return localMilestones.outings_unlocked;
      case 'marketplace':
        return localMilestones.marketplace_unlocked;
      default:
        return true;
    }
  }, [localMilestones]);
  
  // Fetch Golf Coins balance
  const fetchGolfCoins = useCallback(async () => {
    const token = localStorage.getItem('parseeker_session_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/golf-coins/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGolfCoins(data.balance || 0);
      }
    } catch (error) {
      console.log('Could not fetch golf coins');
    }
  }, []);
  
  // Fetch on route change
  useEffect(() => {
    fetchGolfCoins();
  }, [location.pathname, fetchGolfCoins]);
  
  // Listen for custom event to refresh coins (e.g., after winning a game)
  useEffect(() => {
    const handleCoinsUpdate = () => {
      fetchGolfCoins();
    };
    window.addEventListener('golfCoinsUpdated', handleCoinsUpdate);
    return () => window.removeEventListener('golfCoinsUpdated', handleCoinsUpdate);
  }, [fetchGolfCoins]);
  
  // Check if user is qualified (has calculated handicap)
  const isQualified = hasHandicap(handicap.value);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('parseeker_session_token');
    if (!token) return; // Don't fetch if not logged in
    
    try {
      setLoadingNotifications(true);
      const response = await fetch(`${API}/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);
  
  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('parseeker_session_token');
    if (!token) return; // Don't fetch if not logged in
    
    try {
      const response = await fetch(`${API}/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);
  
  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('parseeker_session_token');
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };
  
  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('parseeker_session_token');
      await fetch(`${API}/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };
  
  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
    setShowNotifications(false);
  };
  
  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    // Check for new notifications every 20 seconds
    const interval = setInterval(fetchUnreadCount, 20000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);
  
  // Also fetch when user becomes available
  useEffect(() => {
    if (user?.user_id) {
      fetchUnreadCount();
    }
  }, [user?.user_id, fetchUnreadCount]);
  
  // Fetch full notifications when dropdown opens, and poll while open
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
      // Keep polling while notifications panel is open
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [showNotifications, fetchNotifications]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to top when route changes - ensures header is visible
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Check if path is active (handle legacy routes)
  const isPathActive = (path) => {
    const currentPath = location.pathname;
    
    // Map new paths to existing routes
    const pathMappings = {
      '/home': ['/home', '/'],
      '/play': ['/play', '/add-score'],
      '/games': ['/games', '/tournaments', '/tournaments/skins', '/tournaments/stroke-play', '/tournaments/match-play'],
      '/events': ['/events'],
      '/groups': ['/groups'],
      '/friends': ['/friends'],
      '/chat': ['/chat'],
      '/profile': ['/profile', '/settings', '/subscription'],
    };

    const matchingPaths = pathMappings[path] || [path];
    return matchingPaths.some(p => currentPath === p || currentPath.startsWith(p + '/'));
  };

  // Handle navigation with sound
  const handleNavClick = (path) => {
    soundEffects.playClick();
    
    // Map new paths to existing routes
    const routeMap = {
      '/home': '/home', // Uses new dark theme social feed
      '/play': '/add-score', // Goes to add score page (choose course, add score flow)
      '/games': '/tournaments', // Goes to games selection page (Skins, Stroke Play, Match Play)
      '/events': '/events', // Social Events page
      '/groups': '/groups', // Groups page
      '/friends': '/friends', // Friends page with invite options
      '/chat': '/chat', // Chat/Messenger page
      '/profile': '/settings',
    };

    navigate(routeMap[path] || path);
  };

  // Handle AI Tools navigation
  const handleAIToolClick = (tool) => {
    soundEffects.playClick();
    setShowMoreMenu(false);
    setShowAIToolsSubmenu(false);
    navigate(`/ai-tools?tool=${tool}`);
  };

  // Handle PS Tracer click from More menu
  const handlePSTracerClick = () => {
    soundEffects.playClick();
    setShowMoreMenu(false);
    setShowAIToolsSubmenu(false);
    setShowPSTracer(true);
  };

  return (
    <FullScreenContext.Provider value={{ isFullScreen, setIsFullScreen }}>
      <div ref={shellRef} className="dark-app" data-testid="dark-app-shell" data-fullscreen={isFullScreen}>
        {/* Top Navigation - Hidden in full screen mode */}
        {!isFullScreen && (
          <header ref={headerRef} className="top-nav glass-nav" data-testid="top-nav">
        <div className="top-nav-inner">
          {/* Signed-in account row */}
          <div className={`ps-shell-top-row flex items-center justify-between mb-3${isHome ? ' ps-shell-home-row' : ''}`} data-testid="header-account-row">
            <HeaderUserName user={user} />
            
            {/* Qualified/Qualifying Indicator - Center */}
            <button
              onClick={() => navigate('/dashboard')}
              className="font-black text-base italic tracking-tight px-2 whitespace-nowrap flex-shrink-0"
              style={{
                backgroundImage: isQualified 
                  ? 'linear-gradient(90deg, #22c55e, #facc15)'
                  : 'linear-gradient(90deg, #f97316, #facc15)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: 'none'
              }}
              data-testid="qualified-indicator"
            >
              {isQualified ? 'Qualified' : 'Qualifying'}
            </button>

            {isHome && <HeaderHandicap {...handicap} onClick={() => navigate('/stats')} />}

            {/* Golf Coins Button */}
            <button
              onClick={() => navigate('/golf-coins')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/10 hover:bg-amber-500/20 transition-colors flex-shrink-0"
              data-testid="golf-coins-btn"
            >
              <Coins className="w-4 h-4 text-amber-500" />
              <span className="text-amber-500 font-bold text-sm">
                {golfCoins > 0 ? golfCoins.toLocaleString() : 'Coins'}
              </span>
            </button>
            
            <div className="ps-shell-account-actions flex items-center gap-2 flex-shrink-0">
              {/* Notifications Bell with Dropdown */}
              <div className="relative" ref={notificationsRef}>
                <button 
                  className="relative p-2 rounded-full hover:bg-[var(--ps-bg-hover)] transition-colors"
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-[var(--ps-text-secondary)]" />
                  {(unreadCount > 0 || unreadMessages > 0) && (
                    <span className="notification-badge" data-testid="notifications-unread-count">{(unreadCount + unreadMessages) > 9 ? '9+' : (unreadCount + unreadMessages)}</span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div data-testid="notifications-dropdown" className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-3 border-b border-[var(--ps-border)] flex items-center justify-between">
                      <h3 className="font-bold text-[var(--ps-text-primary)]">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button 
                            onClick={markAllAsRead}
                            data-testid="notifications-mark-all-read"
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Mark all read
                          </button>
                        )}
                        <button 
                          onClick={() => setShowNotifications(false)}
                          data-testid="notifications-close" aria-label="Close notifications"
                          className="text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto">
                      {/* Loading State */}
                      {loadingNotifications && (
                        <div className="px-4 py-8 text-center">
                          <Loader2 className="w-6 h-6 text-[var(--ps-text-muted)] mx-auto animate-spin" />
                        </div>
                      )}
                      
                      {/* Notification Items */}
                      {!loadingNotifications && notifications.map((notification) => {
                        const Icon = getNotificationIcon(notification.type);
                        const colorClass = getNotificationColor(notification.type);
                        return (
                          <button 
                            key={notification.notification_id}
                            data-testid={`notification-item-${notification.notification_id}`}
                            onClick={() => handleNotificationClick(notification)}
                            className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-[var(--ps-bg-hover)] transition-colors text-left border-b border-[var(--ps-border)] ${!notification.read ? 'bg-[var(--ps-bg-elevated)]' : ''}`}
                          >
                            {/* Sender avatar or type icon */}
                            {notification.sender_picture ? (
                              <img 
                                src={notification.sender_picture} 
                                alt="" 
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                                <Icon className="w-5 h-5" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-medium text-[var(--ps-text-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-[var(--ps-text-muted)] truncate">{notification.message}</p>
                              <p className="text-xs text-[var(--ps-text-muted)] mt-0.5">{notification.time_ago}</p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                            )}
                          </button>
                        );
                      })}
                      
                      {/* Legacy: Unread Messages from Chat */}
                      {!loadingNotifications && unreadMessages > 0 && notifications.length === 0 && (
                        <button 
                          onClick={() => { navigate('/chat'); setShowNotifications(false); }}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[var(--ps-bg-hover)] transition-colors text-left border-b border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]"
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--ps-text-primary)]">New Messages</p>
                            <p className="text-xs text-[var(--ps-text-muted)]">You have {unreadMessages} unread message{unreadMessages !== 1 ? 's' : ''}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[var(--ps-text-muted)]" />
                        </button>
                      )}
                      
                      {/* Empty State */}
                      {!loadingNotifications && notifications.length === 0 && unreadMessages === 0 && (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-3 opacity-50" />
                          <p className="text-sm text-[var(--ps-text-muted)]">No notifications yet</p>
                          <p className="text-xs text-[var(--ps-text-muted)] mt-1">You're all caught up!</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-2 bg-[var(--ps-bg-elevated)] text-center border-t border-[var(--ps-border)]">
                        <p className="text-xs text-[var(--ps-text-muted)]">
                          Showing {notifications.length} recent notifications
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <Avatar 
                className="w-9 h-9 cursor-pointer transition-all"
                data-testid="nav-profile-avatar"
                onClick={() => navigate('/settings')}
                style={{
                  background: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)',
                  boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.5), inset -1px -1px 3px rgba(50,50,50,0.3), 0 0 0 2px rgba(40,40,40,0.8)',
                  borderRadius: '50%'
                }}
              >
                <AvatarImage src={user?.picture} />
                <AvatarFallback 
                  className="text-[var(--ps-primary)] text-sm font-bold"
                  style={{
                    background: 'transparent'
                  }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* Navigation Icons */}
          <nav className="top-nav-icons" data-testid="top-nav-icons">
            {topNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`top-nav-item ${isPathActive(item.path) ? 'active' : ''} ${
                  item.highlight === 'amber' ? 'book-highlight' : ''
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
                style={item.highlight === 'amber' ? {
                  color: isPathActive(item.path) ? '#f59e0b' : '#fbbf24',
                } : {}}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
            
            {/* More Menu */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`top-nav-item ${showMoreMenu ? 'active' : ''}`}
                data-testid="nav-more"
              >
                <MoreHorizontal className="w-5 h-5" />
                <span>More</span>
              </button>

              {showMoreMenu && (
                <div className="more-menu" data-testid="more-menu">
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/outings'); setShowMoreMenu(false); }}
                    data-testid="golf-outings-menu-btn"
                  >
                    <Users className="text-purple-500" />
                    Golf Outings
                  </button>
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/wallet'); setShowMoreMenu(false); }}
                    data-testid="golf-wallet-menu-btn"
                  >
                    <Wallet className="text-green-500" />
                    Golf Wallet
                  </button>
                  <div className="more-menu-divider" />
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/history'); setShowMoreMenu(false); }}
                    data-testid="more-score-history"
                  >
                    <History />
                    Score History
                  </button>
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/settlements'); setShowMoreMenu(false); }}
                    data-testid="more-settlements"
                  >
                    <DollarSign />
                    Settlements
                  </button>
                  <div className="more-menu-divider" />
                  
                  {/* AI Tools Submenu */}
                  <div className="relative">
                    <button 
                      className="more-menu-item justify-between"
                      onClick={() => setShowAIToolsSubmenu(!showAIToolsSubmenu)}
                      data-testid="ai-tools-menu-btn"
                    >
                      <span className="flex items-center gap-3">
                        <Sparkles className="text-[var(--ps-gold)]" />
                        AI Tools
                      </span>
                      <ChevronRight className={`w-4 h-4 transition-transform ${showAIToolsSubmenu ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {showAIToolsSubmenu && (
                      <div className="ml-4 border-l-2 border-[var(--ps-border)] pl-2 mt-1 space-y-1" data-testid="ai-tools-submenu">
                        <button 
                          className="more-menu-item text-sm py-2"
                          onClick={() => handleAIToolClick('scanner')}
                          data-testid="ai-scanner-btn"
                        >
                          <Camera className="w-4 h-4" />
                          Scorecard Scanner
                        </button>
                        <button 
                          className="more-menu-item text-sm py-2"
                          onClick={handlePSTracerClick}
                          data-testid="ps-tracer-btn"
                        >
                          <Target className="w-4 h-4 text-yellow-400" />
                          <span className="text-yellow-400">PS Tracer</span>
                        </button>
                        <button 
                          className="more-menu-item text-sm py-2"
                          onClick={() => handleAIToolClick('pin')}
                          data-testid="ai-pin-btn"
                        >
                          <MapPin className="w-4 h-4" />
                          Photo Pin Locator
                        </button>
                        <button 
                          className="more-menu-item text-sm py-2"
                          onClick={() => handleAIToolClick('valuator')}
                          data-testid="ai-valuator-btn"
                        >
                          <DollarSign className="w-4 h-4" />
                          Gear Valuator
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="more-menu-divider" />
                  <button 
                    className="more-menu-item"
                    onClick={() => { setShowTickerSettings(true); setShowMoreMenu(false); }}
                    data-testid="ps-news-menu-btn"
                  >
                    <Newspaper />
                    PS News Ticker
                  </button>
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/settings'); setShowMoreMenu(false); }}
                    data-testid="more-settings"
                  >
                    <Settings />
                    Settings
                  </button>
                  <button 
                    className="more-menu-item"
                    onClick={() => { navigate('/subscription'); setShowMoreMenu(false); }}
                    data-testid="more-subscription"
                  >
                    <CreditCard />
                    Subscription
                  </button>
                  {user?.is_admin && (
                    <>
                      <div className="more-menu-divider" />
                      <button 
                        className="more-menu-item"
                        onClick={() => { navigate('/admin'); setShowMoreMenu(false); }}
                        data-testid="more-admin-dashboard"
                      >
                        <Shield />
                        Admin Dashboard
                      </button>
                      <button className="more-menu-item" data-testid="more-admin-proshop" onClick={() => { navigate('/proshop/admin'); setShowMoreMenu(false); }}><Shield />Admin Pro Shop</button>
                      <button 
                        className="more-menu-item"
                        onClick={() => { navigate('/admin/email-templates'); setShowMoreMenu(false); }}
                        data-testid="more-email-templates"
                      >
                        <Mail />
                        Email Templates
                      </button>
                    </>
                  )}
                  <div className="more-menu-divider" />
                  <button 
                    className="more-menu-item text-red-500"
                    onClick={() => { onLogout?.(); setShowMoreMenu(false); }}
                    data-testid="more-log-out"
                  >
                    <LogOut className="text-red-500" />
                    Log Out
                  </button>
                  <div className="more-menu-divider" />
                  <button className="more-menu-item" onClick={() => { navigate('/support'); setShowMoreMenu(false); }} data-testid="more-help-contact-admin">
                    <Mail className="w-4 h-4" /> Help &amp; Contact Admin
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>
        )}

      {/* Main Content - Full width/height when in full screen mode */}
      <main className={isFullScreen ? "dark-main-content-fullscreen" : `dark-main-content${location.pathname.startsWith('/proshop/dashboard') ? ' course-dashboard-shell' : ''}`} data-testid="app-main-content">
        <AccountLinkConfirmations user={user} />
        {children}
      </main>

      {/* Bottom Navigation with Ticker (Mobile) - Hidden in full screen mode */}
      {!isFullScreen && (
        <nav ref={footerRef} className="bottom-nav glass-nav md:hidden" data-testid="bottom-nav">
        {/* Golf News Ticker - Integrated into bottom nav */}
        <div className="ticker-wrapper">
          <GolfNewsTicker 
            currentPath={location.pathname} 
            externalShowSettings={showTickerSettings}
            onSettingsClose={() => setShowTickerSettings(false)}
          />
        </div>
        <div className="bottom-nav-inner">
          {/* First two nav items (Home, Golfers) */}
          {bottomNavItems.slice(0, 2).map((item) => {
            const unlocked = isUnlocked(item.unlockKey);
            return (
              <button
                key={item.path}
                onClick={() => unlocked ? handleNavClick(item.path) : null}
                className={`bottom-nav-item ${isPathActive(item.path) ? 'active' : ''} ${!unlocked ? 'opacity-50' : ''}`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                disabled={!unlocked}
              >
                <div className="relative">
                  <item.icon className="w-6 h-6" />
                  {!unlocked && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-gray-400" />
                    </div>
                  )}
                </div>
                <span>{item.label}</span>
              </button>
            );
          })}
          
          {/* Center Glowing Blue Plus Button - Quick Post */}
          <button
            onClick={() => setShowGolfShots(true)}
            className="quick-post-btn"
            data-testid="bottom-nav-quick-post"
            aria-label="Quick Post - Open Camera"
          >
            <div className="quick-post-btn-inner">
              <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
          </button>
          
          {/* Last two nav items (Chat, Profile) */}
          {bottomNavItems.slice(2).map((item) => {
            const unlocked = isUnlocked(item.unlockKey);
            return (
              <button
                key={item.path}
                onClick={() => unlocked ? handleNavClick(item.path) : null}
                className={`bottom-nav-item ${isPathActive(item.path) ? 'active' : ''} ${!unlocked ? 'opacity-50' : ''}`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                disabled={!unlocked}
              >
                <div className="relative">
                  <item.icon className="w-6 h-6" />
                  {!unlocked && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-gray-400" />
                    </div>
                  )}
                  {unlocked && item.path === '/chat' && unreadMessages > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                      data-testid="chat-unread-badge"
                    >
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </div>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      )}

      {/* PS Tracer Modal - accessible from More menu */}
      <ShotTracerModal 
        isOpen={showPSTracer}
        onClose={() => setShowPSTracer(false)}
        onVideoCreated={(videoUrl) => {
          // Navigate to feed with the video ready for posting
          setShowPSTracer(false);
          navigate('/', { state: { tracerVideoUrl: videoUrl } });
        }}
      />
      
      {/* GolfShots Quick Post Modal - accessible from bottom nav plus button */}
      <GolfShotsModal 
        isOpen={showGolfShots}
        onClose={() => setShowGolfShots(false)}
        onGifCreated={(mediaUrl, mediaType) => {
          // Close the GolfShots modal
          setShowGolfShots(false);
          
          // Store media in sessionStorage so SocialFeedPage can pick it up after navigation
          sessionStorage.setItem('quickPostMedia', JSON.stringify({
            mediaUrl,
            mediaType: mediaType || 'image'
          }));
          
          // Also dispatch event for immediate use if already on feed page
          window.dispatchEvent(new CustomEvent('quickPostMedia', {
            detail: { 
              mediaUrl, 
              mediaType: mediaType || 'image' 
            }
          }));
          
          // Navigate to feed where SocialFeedPage handles the post creation
          if (location.pathname !== '/feed') {
            navigate('/feed');
          }
        }}
      />
    </div>
    </FullScreenContext.Provider>
  );
};

export default DarkAppShell;
